import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { Locale } from '../../../shared/model/locale.model';
import { Product } from '../../../shared/model/product.model';
import { FormGroup, ControlContainer, NgForm } from '@angular/forms';

interface ProgramHoursOption {
  code: string;
  translationKey: string;
  cronValue: string;
}

@Component({
  selector: 'app-product-homepage',
  templateUrl: './product-homepage.component.html',
  styleUrls: ['./product-homepage.component.scss', '../../../shared/shared.styles.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class ProductHomepageComponent implements OnInit, OnChanges {
  @Input() mode: string;
  @Input() locale: Locale;
  @Input() product: Product;
  @Input() frm: FormGroup;
  // Notifies the parent on panel expand/collapse so it can flip the product into
  // dispatchable mode (the same hook used by Dispatch/Performance/Customer/Utility
  // panels). Required so the home page configuration is persisted via /products
  // (with prez_conf sent to COM) instead of /product-stubs (Atlas-only).
  @Output('toggleHomePageProduct') toggleHomePageProduct: EventEmitter<any> = new EventEmitter();

  panelOpenState = false;
  programHoursType: string | null;

  // Program Hours dropdown options. Each option carries a default CRON expression.
  // The "CUSTOM" option leaves the CRON field empty and editable by the user.
  programHoursOptions: ProgramHoursOption[] = [
    {
      code: 'ALL_YEAR_WEEKLY',
      translationKey: 'common.home_page_config.program_hours.all_year_weekly',
      cronValue: '* * * ? * *',
    },
    {
      code: 'ALL_YEAR_WEEKDAYS',
      translationKey: 'common.home_page_config.program_hours.all_year_weekdays',
      cronValue: '* * * ? * 1-5',
    },
    {
      code: 'CUSTOM',
      translationKey: 'common.home_page_config.program_hours.custom',
      cronValue: '',
    },
  ];

  constructor() {}

  ngOnInit(): void {
    this.syncProgramHoursTypeFromProduct();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The parent component initially binds an empty Product, then replaces it
    // with the one loaded from the API. ngOnInit only fires on the first (empty)
    // value, so we also need to react to subsequent input changes here.
    if (changes.product) {
      this.syncProgramHoursTypeFromProduct();
    }
  }

  /**
   * Derives the dropdown selection from the CRON value currently stored on the product:
   * - matches one of the preset CRON values -> select that preset
   * - any other non-empty value             -> select CUSTOM
   * - empty/missing                         -> leave the dropdown empty
   */
  private syncProgramHoursTypeFromProduct(): void {
    if (!this.product || !this.product.prezConf) {
      return;
    }

    const existingCron = this.product.prezConf.programHours;
    if (existingCron) {
      const match = this.programHoursOptions.find(
        (opt) => opt.code !== 'CUSTOM' && opt.cronValue === existingCron,
      );
      this.programHoursType = match ? match.code : 'CUSTOM';
    } else {
      this.programHoursType = null;
    }
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  /**
   * Returns true when a value other than null/empty has been chosen in the
   * Program Hours dropdown. Drives the "required" / validation state of the
   * Program Hours CRON text field.
   */
  get isProgramHoursTypeSelected(): boolean {
    return !!(this.product && this.product.prezConf && this.programHoursType);
  }

  /**
   * The CRON field is only editable when the user explicitly chooses "Custom".
   * For preset options the field is read-only and displays the preset CRON value.
   */
  get isCustomProgramHours(): boolean {
    return (
      this.product &&
      this.product.prezConf &&
      this.programHoursType === 'CUSTOM'
    );
  }

  /**
   * Reacts to the Program Hours dropdown change:
   * - preset option -> writes the preset CRON value into the CRON text field
   * - CUSTOM        -> clears the CRON value so the user can type a custom one
   * - cleared       -> removes the CRON value entirely from the payload
   */
  onProgramHoursTypeChange(selectedCode: string | null): void {
    this.programHoursType = selectedCode;
    if (!this.product || !this.product.prezConf) {
      return;
    }

    // Dropdown emptied (placeholder reselected) or unknown option: send an
    // explicit null so a previously saved CRON gets cleared on the backend.
    // (A deleted/undefined key is dropped by JSON.stringify and the backend
    // would keep the old value.) The API treats program_hours as optional with
    // checkFalsy, so null skips CRON validation.
    if (!selectedCode) {
      this.product.prezConf.programHours = null;
      return;
    }

    const option = this.programHoursOptions.find((opt) => opt.code === selectedCode);
    if (!option) {
      this.product.prezConf.programHours = null;
      return;
    }

    // Preset selected: copy its CRON. Custom: clear so the user types their own.
    this.product.prezConf.programHours = option.code === 'CUSTOM' ? '' : option.cronValue;
  }

  /**
   * Resolves the dropdown code stored on the product to its translatable label,
   * used in the read-only VIEW mode.
   */
  get programHoursTypeForProduct(): string {
    if (this.product && this.product.prezConf && this.programHoursType) {
      const option = this.programHoursOptions.find(
        (opt) => opt.code === this.programHoursType,
      );
      if (option) {
        return option.translationKey;
      }
    }
    return '';
  }

  onPanelExpand(): void {
    this.toggleHomePageProduct.emit();
  }
}